import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const InventoryCost = () => {
  // State variables for Inventory Cost calculation
  const [purchaseCost, setPurchaseCost] = useState(0); // Cost of purchasing inventory
  const [orderingCost, setOrderingCost] = useState(0); // Cost associated with ordering inventory
  const [holdingCost, setHoldingCost] = useState(0); // Cost of storing inventory
  const [totalCost, setTotalCost] = useState(0); // Calculated total inventory cost

  // Event handlers to update state values
  const handlePurchaseCostChange = (e) =>
    setPurchaseCost(parseFloat(e.target.value) || 0);
  const handleOrderingCostChange = (e) =>
    setOrderingCost(parseFloat(e.target.value) || 0);
  const handleHoldingCostChange = (e) =>
    setHoldingCost(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate inventory cost when any input changes
  useEffect(() => {
    const inventoryCost = purchaseCost + orderingCost + holdingCost;
    setTotalCost(inventoryCost);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [purchaseCost, orderingCost, holdingCost]);

  return (
    <Layout title="Inventory Cost Calculator" path="inventory-cost">
      <section className="section-padding" id="inventory-cost-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Inventory Cost Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Inventory Cost Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Purchase Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="purchaseCost" className="form-label">
                        Purchase Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Purchase Cost:</strong> The cost of acquiring the inventory."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="purchaseCost"
                        placeholder="Enter purchase cost"
                        value={purchaseCost || ""}
                        onChange={handlePurchaseCostChange}
                      />
                    </div>

                    {/* Ordering Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="orderingCost" className="form-label">
                        Ordering Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Ordering Cost:</strong> The expenses incurred during the procurement process."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="orderingCost"
                        placeholder="Enter ordering cost"
                        value={orderingCost || ""}
                        onChange={handleOrderingCostChange}
                      />
                    </div>

                    {/* Holding Cost Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="holdingCost" className="form-label">
                        Holding Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Holding Cost:</strong> The cost of storing and maintaining the inventory."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="holdingCost"
                        placeholder="Enter holding cost"
                        value={holdingCost || ""}
                        onChange={handleHoldingCostChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Inventory Cost:{" "}
                        <span className="fw-bold ms-3">
                          {totalCost === 0 ? "0.00" : totalCost.toFixed(2)} ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Inventory Cost Calculator
                </h3>

                {/* What is an Inventory Cost Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is an Inventory Cost Calculator?
                </h4>
                <p>
                  An **Inventory Cost Calculator** helps businesses estimate the
                  total cost of maintaining inventory by adding together the
                  purchase cost, ordering cost, and holding cost. This tool
                  helps in determining the overall costs associated with
                  managing inventory.
                </p>

                {/* Why Use an Inventory Cost Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use an Inventory Cost Calculator?
                </h4>
                <p>
                  This calculator is essential for businesses to optimize
                  inventory levels. By factoring in all relevant costs,
                  businesses can ensure they are managing their inventory
                  efficiently and reducing unnecessary overhead.
                </p>

                {/* Benefits of Using an Inventory Cost Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using an Inventory Cost Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Helps Optimize Inventory Levels:</strong> Assists in
                    determining the most cost-effective inventory levels based
                    on purchase, ordering, and holding costs.
                  </li>
                  <li>
                    <strong>Reduces Overhead:</strong> Helps businesses avoid
                    excess inventory and unnecessary storage costs, leading to
                    reduced overhead.
                  </li>
                  <li>
                    <strong>Supports Budgeting and Cost Management:</strong>{" "}
                    Provides insights for businesses to better manage and
                    allocate their budget for inventory-related expenses.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use an Inventory Cost Calculator?
                </h4>
                <p>
                  The Inventory Cost Calculator is ideal for businesses involved
                  in retail, manufacturing, and warehousing, including:
                </p>
                <ul>
                  <li>
                    <strong>Retail Businesses:</strong> Helps in managing
                    inventory levels and minimizing costs associated with stock.
                  </li>
                  <li>
                    <strong>Manufacturers:</strong> Useful for managing raw
                    materials, production schedules, and overall inventory
                    management.
                  </li>
                  <li>
                    <strong>Warehouses:</strong> Helps in managing the storage
                    and handling costs of goods in warehouses.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How does the purchase cost impact inventory costs?
                </h4>
                <p>
                  The purchase cost is a direct expense for acquiring inventory.
                  By accurately factoring in the purchase cost, businesses can
                  ensure they are not overspending on the inventory they
                  acquire.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is it important to track ordering and holding costs?
                </h4>
                <p>
                  Ordering costs are incurred each time an order is placed,
                  while holding costs are associated with storing inventory.
                  Tracking these costs helps businesses manage their inventory
                  flow and avoid tying up too much capital in unsold goods.
                </p>

                <h4 className="mt-4 mb-3">
                  Can this calculator help reduce inventory waste?
                </h4>
                <p>
                  Yes, by calculating the total costs, this tool helps
                  businesses make informed decisions on how much inventory to
                  hold, reducing excess inventory and minimizing waste.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default InventoryCost;
